// The chart furniture's controller (R548, ui-map-interface.md §17).
//
// It owns the two engine layers, the DOM host, the one context, the reading
// and the fade. Everything a piece is allowed to know arrives through the
// context; everything the client is allowed to ask of the furniture is a
// method here. `MapScene` builds one and calls four of them: `setReading`,
// `setFilming`, `invalidate` and `frame`.
//
// **The common gate.** A piece draws on a frame when all five hold:
//
//   1. its form-factor gate is on (`mobile` on the sheet, else `desktop`);
//   2. the camera scale is inside its zoom band;
//   3. its table says the reading being drawn is `on`;
//   4. no replay film has the glass, or the piece is `film: 'keep'`;
//   5. its own `visible?(ctx)` says yes.
//
// The gate is computed ONE time a frame, here, and both layers read the
// answer. A gate computed twice is a gate that can disagree with itself
// between the rebuild and the pack.
//
// **The tick runs from two places, and is idempotent.** `MapScene.frame`
// calls `frame()` on every frame it runs — but a replay film takes the frame
// away from the scene (R531), and the terrain furniture stays on the glass
// under a film. So the layers call the same tick at the top of their own
// update. Nothing in the tick accumulates: the fade reads the frame's own
// clock and every rebuild reason is an edge, so the second call of a frame
// costs a handful of compares and changes nothing.
//
// **`off` costs nothing** (§10.3). Both layers are hidden and the DOM host
// is hidden, so the loop neither updates nor draws them.
//
// **One piece draws under `off`: the map markers** (R1294, §17.1). The pack
// names it with `off: true`, and no piece name stands in this file. Under
// `off` the tick gates the `off` pieces alone: every other piece is gated
// out, takes no pick and does not build. A layer is visible under `off`
// only while an `off` piece in it passes the gate, so an empty marker list
// costs the tick and nothing else.
//
// **A client heat map keeps its piece under `off`** (R2870). While the
// CURRENTS heat map is on, card 07 (`current_arrows`) runs under `off` as
// an `off` piece does, and card 09 (`depth_tints`) while DEPTH is on: the
// player asked for that field by name. The piece keeps the reading drawn
// last, as an `off` piece does.

import {
  BANK_NAMES, REGION_NAMES, RIDGE_NAMES, TRENCH_NAMES,
} from '../../gen/balance';
import { CHART } from '../../gen/presentation';
import { tideStepAt } from '../../deep';
import type { FeatureFlag } from '../../gen/featureflags';
import { heatMode, type GameState } from '../../state';
import { HeatmapKind } from '../../gen/proto/aquaphobia';
import { assignFeatureNames, trenchGrants } from '../../render/feature-names';
import { terrainFeatures, type Feature } from '../../render/terrain-features';
import { seamountOnBody } from '../../seamounts';
import type { RidgeAt } from '../../ui/cards/terrain';
import { isSheet } from '../../ui/layout';
import type { CornerGrid } from '../../ui/cornergrid';
import { DEFAULT_SETTINGS } from '../../ui/settings';
import { COLS, ROWS } from '../../world';
import type { FrameInfo, ViewRect } from '../layer';
import type { BatchSource } from '../layers/batches';
import type { Hit } from '../hit';
import type { SmallMark } from '../smallmarks';
import { setHostAlpha, setHostVisible } from './dom';
import { ChartGlyphs } from './glyphs';
import { ChartLayer } from './layer';
import { CHART_PIECES } from './registry';
import { bandScalesInto } from './steps';
import {
  isDomPiece,
  type AnyPiece, type ChartContext, type ChartReading, type ChartScene,
  type ChartSetting, type DomPiece, type Piece, type PickHit,
  type RebuildReason,
} from './context';

/** The two layer slots the furniture fills, in draw order. */
const CHART_SEA_SLOT = 'chartsea';
const CHART_SCREEN_SLOT = 'chartscreen';

/**
 * What the furniture reads of the page around it.
 *
 * `fast/main.ts` builds one from the overlay's host element, the tooltip and
 * the command module; an arcade match and a test hand over nothing, and
 * every reader below then answers "no pointer, no aim, no host".
 */
export interface ChartHost {
  /**
   * The `#furniture` element inside `#ui`, where a DOM piece mounts.
   *
   * The page reads it lazily, because the chrome is built after the scene:
   * nothing here touches it before the first frame.
   */
  root: HTMLElement;
  /** What the pointer is over, or null. */
  hover(): Hit | null;
  /** Where the pointer is over the canvas in CSS px, or null. */
  hoverAt(): { x: number; y: number } | null;
  /** The cell under the pointer, or −1. */
  hoverCell(): number;
  /** The point of interest whose card the peek shows, or 0 (R2955). */
  peekPoi?(): number;
  /** The armed aim point in WORLD px, or null. */
  aim(): { x: number; y: number } | null;
  /** The console recomputed its geometry, so a corner piece must move. */
  onCluster(fn: () => void): void;
  /** The corner grid of the map edge, kept until a layout change (R1812). */
  edges?(): CornerGrid | null;
  /** NEEDS YOU stands open in the INBOX, so the inbox pins draw (R1846). */
  needsPins?(): boolean;
  /** The message whose inbox pin is lit, or 0 (R1846). */
  needsLit?(): number;
  /** The radius of the harbour limits, 0 while they do not play (R2585). */
  harbourPx?(): number;
  /** The world plays the NPC side, so card 50 marks the narrows (R2637). */
  npcWater?(): boolean;
  /** The world plays the treasure, so card 69 marks the seeded caches (R2714). */
  cachesOn?(): boolean;
}

/** What `ChartFurniture` is built with. */
export interface ChartDeps {
  /** The batches. `Engine` satisfies it, and a test hands over a recorder. */
  src: BatchSource;
  /** The pack. The shipped table when absent; a test hands over one that
   *  turns a retired piece on again (R561). */
  cfg?: typeof CHART;
  /** The cache of server truth. The chart reads it and writes none of it. */
  state: GameState;
  /** The map, as the chart reads it. */
  scene: ChartScene;
  /** The page around the chart, or nothing. */
  host?: ChartHost | null;
  /** The pieces. The registry's list without an answer. */
  pieces?: readonly AnyPiece[];
  /**
   * The atlas glyph refresh (R548). The two vendored faces land after the
   * first frames, and a run laid out against the fallback advance no longer
   * fits the cells it draws — so every piece that writes text packs again.
   */
  onGlyphs?(fn: () => void): () => void;
  /**
   * The atlas repack. A grow moves every cell, and a static piece holds the
   * UVs of its last pack — so EVERY piece packs again (its `'atlas'` reason).
   */
  onRepacked?(fn: () => void): () => void;
  /** The form factor. `isSheet()` without an answer. */
  sheet?(): boolean;
  /** The reading to start at. `off` without an answer. */
  reading?: ChartSetting;
}

/**
 * What a Chart theme says about the pieces (R806): piece id → reading →
 * the tunable keys it sets. `[chart.theme] tunable` in `chart.toml` names
 * the keys a theme may set; the gate reads `on`.
 */
export type ChartPieceOverlay = Readonly<Record<string,
  Readonly<Partial<Record<ChartReading, Readonly<{ on?: boolean }>>>>>>;

/**
 * The reading an `off` piece draws in when the chart was never on: the
 * default of the setting, as the workshop preview takes it (R815).
 */
const DEFAULT_READING: ChartReading =
  DEFAULT_SETTINGS.chart === 'off' ? 'modern' : DEFAULT_SETTINGS.chart;

/** An empty rect, so the context is whole before the first frame. */
function emptyRect(): ViewRect { return { x0: 0, y0: 0, x1: 0, y1: 0 }; }

export class ChartFurniture {
  /** The water pieces: under the beast and every actionable object (§3). */
  readonly sea: ChartLayer;
  /** The screen and selection pieces: over the fleets, under the ambience. */
  readonly screen: ChartLayer;

  private readonly d: ChartDeps;
  private readonly pieces: readonly AnyPiece[];
  /**
   * The pieces that draw under the setting `off` too (R1294): the pack's
   * `off: true`. Empty for a pack that names none, and then `off` ticks
   * nothing at all.
   */
  private readonly keep: readonly AnyPiece[];
  private readonly keepSet: ReadonlySet<AnyPiece>;
  /**
   * The piece of each client heat map (R2870): card 07 for CURRENTS and
   * card 09 for DEPTH. The piece of the active map runs under `off` too,
   * because the player asked for its field by name.
   */
  private readonly currentsPiece: AnyPiece | undefined;
  private readonly depthPiece: AnyPiece | undefined;
  /** The heat piece the active map keeps under `off`, or null. */
  private heatKept: AnyPiece | null = null;
  /** The pieces `off` asks: `keep`, and the heat piece while one stands.
   *  Built again on a change of the heat piece alone, so a frame
   *  allocates nothing. */
  private offAsked: readonly AnyPiece[] = [];
  /** Whether the DOM host stands visible now; null before the first write. */
  private hostShown: boolean | null = null;
  /** The border band (card 39), or nothing when the roster holds none. */
  private readonly bandPiece: AnyPiece | undefined;
  private readonly ctx: ChartContext;

  /** The gate's answer for every piece, computed once a frame. */
  private readonly gateOn = new Map<AnyPiece, boolean>();
  /** A Chart theme's word over the pack's `on`, or null (R806). */
  private overlay: ChartPieceOverlay | null = null;
  /** The pieces that must build again before they pack. */
  private readonly stale = new Set<AnyPiece>();
  /**
   * The pieces that threw (R548). A piece of furniture must never take the
   * map down: one that throws in a build, a pack or a render is dropped from
   * every gate, named once on the console, and tried again at the next
   * hello. The frame loop above it never sees the throw.
   */
  private readonly broken = new Set<AnyPiece>();
  /** The `chart` knob's list, or null for every piece (§17.2). */
  private only: ReadonlySet<string> | null = null;
  /** The pieces of the groups the VIEW panel hides (R1812), and their key. */
  private hidden: ReadonlySet<string> = new Set();
  private hiddenKey = '';
  /** The list the last `setHidden` call handed over, for its identity test. */
  private hiddenList: readonly string[] | null = null;
  /** The group the heat mode of that call showed (R2870), or none. */
  private hiddenHeat: string | undefined = undefined;
  /** The pieces that have built at least one time. */
  private readonly built = new Set<AnyPiece>();
  /** The DOM pieces standing in the host now. */
  private readonly mounted = new Set<DomPiece>();
  /** Whether each piece's zoom band held at the last tick. */
  private readonly inBand = new Map<AnyPiece, boolean>();

  /** The stored pick. `off` draws the `off` pieces alone. */
  private setting: ChartSetting;
  /**
   * The reading being DRAWN. It flips at the middle of a fade. Under `off`
   * it is the reading drawn last, which an `off` piece keeps drawing in.
   */
  private drawn: ChartReading = DEFAULT_READING;
  private fading = false;
  private fadeArmed = false;
  private fadeStartS = 0;

  private filmingNow = false;
  /** The frame the tick last ran for, so it runs ONE time a frame. */
  private tickedFrame: FrameInfo | null = null;
  private tickedAt = -1;
  private lastSelection: unknown = null;
  private lastHover: Hit | null = null;
  private lastHoverCell = -1;
  private lastPeekPoi = 0;
  private lastMinute = -1;
  /** The tide step of the last tick (R2616), -1 before the first. */
  private lastTide = -1;
  /** The wreck list and its length at the last compare (R801). */
  private lastWrecks: readonly unknown[] | null = null;
  private lastWreckCount = -1;
  /** The map marker list at the last compare (R1292). */
  private lastMarkers: readonly unknown[] | null = null;
  /** The heat mode at the last compare (R1806). */
  private lastHeat = -1;
  private disposed = false;

  private readonly dropGlyphs: (() => void) | null = null;
  private readonly dropRepack: (() => void) | null = null;
  private readonly onResize: () => void;

  constructor(deps: ChartDeps) {
    this.d = deps;
    this.pieces = deps.pieces ?? CHART_PIECES;
    this.bandPiece = this.pieces.find((p) => p.id === 'border_band');
    const cfg = deps.cfg ?? CHART;
    this.keep = this.pieces.filter((p) => cfg.pieces[p.id]?.off === true);
    this.keepSet = new Set(this.keep);
    this.offAsked = this.keep;
    this.currentsPiece = this.pieces.find((p) => p.id === 'current_arrows');
    this.depthPiece = this.pieces.find((p) => p.id === 'depth_tints');
    this.setting = deps.reading ?? 'off';
    if (this.setting !== 'off') this.drawn = this.setting;

    this.ctx = {
      reading: this.drawn,
      sheet: this.sheetNow(),
      zoom: 1,
      stepScale: bandScalesInto(1, new Float32Array(4)),
      view: emptyRect(),
      cull: emptyRect(),
      cam: {
        viewProj: new Float32Array(9), viewInv: new Float32Array(9),
        zoom: 1, dpr: 1, pxPerWorld: 1, w: 0, h: 0, shakeX: 0, shakeY: 0,
      },
      timeS: 0,
      state: deps.state,
      scene: deps.scene,
      selection: null,
      hover: null,
      hoverAt: null,
      hoverCell: -1,
      peekPoi: 0,
      terrain: null,
      names: null,
      glyphs: new ChartGlyphs(deps.src),
      cfg,
      worldClockS: 0,
      filming: false,
      aim: null,
      bandOn: false,
      edges: null,
      needsPins: false,
      needsLit: 0,
      harbourPx: 0,
      npcWater: false,
      cachesOn: false,
      hiddenGroups: [],
    };

    const layerDeps = {
      ctx: () => this.ctx,
      gate: (p: Piece) => this.shows(p),
      tick: (frame: FrameInfo) => this.tick(frame),
      fail: (p: Piece, phase: string, err: unknown) => this.fail(p, phase, err),
    };
    this.sea = new ChartLayer(CHART_SEA_SLOT, deps.src, layerDeps);
    this.screen = new ChartLayer(CHART_SCREEN_SLOT, deps.src, layerDeps);
    for (const p of this.pieces) {
      if (isDomPiece(p)) continue;
      this.layerOf(p).add(p);
    }
    // The LAYERS only. The DOM host is not touched here: the chrome that
    // holds it is built after the scene, and the host is read from the
    // first `setReading` on. Under `off` an `off` piece shows its layer at
    // the first tick that gates it on.
    const on = this.setting !== 'off';
    this.sea.setVisible(on);
    this.screen.setVisible(on);

    // The console's geometry and the window's size both move a corner piece,
    // and the two vendored faces land after the first frames. All three are
    // events, never a poll (§10.3).
    deps.host?.onCluster(() => this.invalidate('layout'));
    this.onResize = () => this.invalidate('resize');
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
    }
    if (deps.onGlyphs) this.dropGlyphs = deps.onGlyphs(() => this.invalidate('font'));
    if (deps.onRepacked) this.dropRepack = deps.onRepacked(() => this.invalidate('atlas'));
  }

  /** The stored pick, `off` included. */
  get reading(): ChartSetting { return this.setting; }

  /** The ridge of each rock cell of this world, read with the terrain. */
  private readonly ridgeOf = new Map<number, Feature>();

  /**
   * The ridge that holds a cell and its chart name, or null (§17.3.18).
   * The ridge peek draws its elevation from it (round 086 card 06 b).
   */
  ridgeAt(cell: number): RidgeAt & { body: Feature } | null {
    const f = this.ridgeOf.get(cell);
    if (!f) return null;
    // A named seamount gives the ridge its name, and an own find with no
    // name gives the peek its NAME press (R2633).
    const st = this.d.state;
    const seamount = seamountOnBody(f.cells, st.seamounts.list);
    const find = seamount ? undefined : seamountOnBody(f.cells, st.empire?.seamountFinds ?? []);
    return { body: f, name: seamount?.name || (this.ctx.names?.nameOf(f.id) ?? ''), seamount, find };
  }

  /** The reading being drawn now, which a fade flips at its middle. */
  get drawnReading(): ChartReading { return this.drawn; }

  /** Is a reading change ramping now? */
  get isFading(): boolean { return this.fading; }

  /** The gate's answer for one piece on the last tick. The tests read it. */
  shows(p: AnyPiece): boolean { return this.gateOn.get(p) === true; }

  // -- the reading ---------------------------------------------------------

  /**
   * Takes the reader's pick.
   *
   * A change between the two readings ramps every batch and the DOM host
   * down over half of `chart.fade_sec`, flips the reading, and ramps back up.
   * A change to or from `off` is not a fade: `off` is the absence of the
   * furniture, and a reader who switched it off waits for nothing.
   */
  setReading(next: ChartSetting): void {
    if (next === this.setting) return;
    const before = this.setting;
    this.setting = next;
    if (next !== 'off' && before !== 'off') {
      // Reading to reading: the ramp starts at the next tick, because this
      // call does not know what the frame clock stands at.
      this.fading = true;
      this.fadeArmed = true;
      this.applyVisible();
      return;
    }
    // To or from `off`: no ramp.
    this.fading = false;
    this.fadeArmed = false;
    this.setAlpha(1);
    if (next === 'off') {
      // Every other piece leaves the gate now, so no pick finds it and its
      // layer repacks without it; an `off` piece keeps its answer until the
      // next tick.
      for (const p of this.pieces) {
        if (!this.keepSet.has(p)) this.gateOn.set(p, false);
      }
      this.applyVisible();
      return;
    }
    this.drawn = next;
    this.ctx.reading = next;
    this.applyVisible();
    this.invalidate('reading');
  }

  /**
   * A replay film has the glass (R531).
   *
   * The terrain furniture is the sea the fight happened in and stays;
   * everything that belongs to the season running now is gated out. The flag
   * reaches the furniture on every frame, film or no film, because a film
   * takes the scene's own frame away.
   */
  setFilming(on: boolean): void { this.filmingNow = on; }

  // -- the rebuild reasons -------------------------------------------------

  /**
   * Marks every piece that answers this reason.
   *
   * The piece builds again on the next tick, and a static piece's layer
   * repacks with it. A piece that is gated off keeps the mark and builds on
   * the frame it comes back.
   */
  invalidate(reason: RebuildReason): void {
    if (this.disposed) return;
    if (reason === 'hello') { this.readTerrain(); this.broken.clear(); this.hiddenList = null; }
    // `atlas` needs no piece to name it: every packed UV is stale.
    for (const p of this.pieces) {
      if (reason === 'atlas' || p.rebuildOn.includes(reason)) this.stale.add(p);
    }
  }

  // -- the frame -----------------------------------------------------------

  /**
   * The per-frame tick, from `MapScene.frame`.
   *
   * `filming` is the page's answer for this frame. It is optional, because
   * the layers call the same tick and carry no such answer.
   */
  frame(info: FrameInfo, filming?: boolean): void {
    if (filming !== undefined) this.filmingNow = filming;
    this.tick(info);
  }

  /**
   * Refreshes the context, advances the fade, computes the gate and builds
   * whatever went stale.
   *
   * It is idempotent inside one frame: the fade reads the frame's own clock
   * and every reason below is an edge.
   */
  private tick(info: FrameInfo): void {
    const off = this.setting === 'off';
    this.syncHeatKept();
    if (this.disposed || (off && this.offAsked.length === 0)) return;
    // The scene and both layers all ask for the tick, and the loop hands
    // every one of them the SAME frame object at the same clock. The tick
    // is safe to run again, but the gate is thirty-five answers and there is
    // no reason to compute it three times (§10.3).
    if (this.tickedFrame === info && this.tickedAt === info.timeS) return;
    this.tickedFrame = info;
    this.tickedAt = info.timeS;
    const ctx = this.ctx;
    const d = this.d;

    // The context, rewritten in place (R131).
    ctx.sheet = this.sheetNow();
    ctx.zoom = info.cam.zoom;
    // The screen band of the chart words: one scale per step per frame,
    // which the layers hand to the quad shader as one uniform (R1796).
    bandScalesInto(ctx.zoom, ctx.stepScale);
    ctx.view = info.view;
    ctx.cull = info.cull;
    ctx.cam = info.cam;
    ctx.timeS = info.timeS;
    ctx.filming = this.filmingNow;
    ctx.worldClockS = d.state.worldClockSec();
    ctx.hover = d.host?.hover() ?? null;
    ctx.hoverAt = d.host?.hoverAt() ?? null;
    ctx.hoverCell = d.host?.hoverCell() ?? -1;
    ctx.peekPoi = d.host?.peekPoi?.() ?? 0;
    ctx.aim = d.host?.aim() ?? null;
    ctx.edges = d.host?.edges?.() ?? null;
    // The inbox pins read it in their gate, and the layer repacks on the
    // edge of the gate, so the flag needs no reason of its own (R1846).
    ctx.needsPins = d.host?.needsPins?.() ?? false;
    // The lit pin: the piece finds a new one by one compare (`outgrown`).
    ctx.needsLit = d.host?.needsLit?.() ?? 0;
    // Card 28 answers a new radius with its own edge (`outgrown`).
    ctx.harbourPx = d.host?.harbourPx?.() ?? 0;
    // Card 50 answers the edge of the flag with its own gate (R2637).
    ctx.npcWater = d.host?.npcWater?.() ?? false;
    // Card 69 answers the edge of the flag with its own edge (R2714).
    ctx.cachesOn = d.host?.cachesOn?.() ?? false;
    ctx.selection = d.state.selection;

    this.advanceFade(info.timeS);

    // The edges. Each one is a compare, and none of them is a poll.
    if (ctx.selection !== this.lastSelection) {
      this.lastSelection = ctx.selection;
      this.invalidate('selection');
    }
    // A press on a point of interest peeks its card without a move, so
    // the peeked point is an edge of the hover too (R2955).
    if (ctx.hover !== this.lastHover || ctx.hoverCell !== this.lastHoverCell
      || ctx.peekPoi !== this.lastPeekPoi) {
      this.lastHover = ctx.hover;
      this.lastHoverCell = ctx.hoverCell;
      this.lastPeekPoi = ctx.peekPoi;
      this.invalidate('hover');
    }
    const minute = Math.floor(ctx.worldClockS / 60);
    if (minute !== this.lastMinute) {
      this.lastMinute = minute;
      this.invalidate('minute');
    }
    // A new tide step (R2616): the tide clock of the legend card writes
    // again. A lookup in the table of the catalog, no allocation.
    const rules = d.state.rules;
    const tide = tideStepAt(d.state.tideElapsedSec(), rules.tidePeriodSec, rules.tideSteps);
    if (tide !== this.lastTide) {
      this.lastTide = tide;
      this.invalidate('tide');
    }
    // A hello replaces the wreck list and a Wrecks frame appends to it, so
    // the array or its length moved (R801).
    const wrecks = d.state.wrecks;
    if (wrecks !== this.lastWrecks || wrecks.length !== this.lastWreckCount) {
      this.lastWrecks = wrecks;
      this.lastWreckCount = wrecks.length;
      this.invalidate('wrecks');
    }
    // A Markers frame REPLACES the list (R1292), so the array moved.
    const markers = d.state.mapMarkers;
    if (markers !== this.lastMarkers) {
      this.lastMarkers = markers;
      this.invalidate('markers');
    }
    // The heat mode moved: the legend card shows its HEAT section while a
    // mode paints (R1806).
    if (d.state.heatmap !== this.lastHeat) {
      this.lastHeat = d.state.heatmap;
      this.invalidate('heat');
    }
    // Under `off` only the `off` pieces are asked, so the other pieces cost
    // no compare. Their marks stand, and they build on the reading's return.
    // The piece of an active client heat map is asked too (R2870).
    const asked = off ? this.offAsked : this.pieces;
    let banded = false;
    for (const p of asked) {
      const t = ctx.cfg.pieces[p.id];
      const on = ctx.zoom >= t.zoomMin && ctx.zoom <= t.zoomMax;
      if (this.inBand.get(p) !== on) { this.inBand.set(p, on); banded = true; }
    }
    if (banded) this.invalidate('zoomband');

    // The border band decides where the graticule writes its figures, so its
    // own gate is answered before any other piece reads `bandOn`.
    ctx.bandOn = this.bandPiece ? this.gateOf(this.bandPiece) : false;

    // The gate, once, for every piece; then the builds.
    for (const p of asked) {
      const on = this.gateOf(p);
      this.gateOn.set(p, on);
      if (isDomPiece(p)) { this.syncDom(p, on); continue; }
      if (!on) continue;
      if (this.stale.has(p) || !this.built.has(p) || p.outgrown?.(ctx)) {
        this.stale.delete(p);
        this.built.add(p);
        if (!this.guard(p, 'rebuild', () => p.rebuild(ctx))) continue;
        if (p.pack === 'static') this.layerOf(p).markDirty();
      }
    }
    if (off) this.applyVisible();
  }

  /** The nearest mark of either tier under a world point, or null. */
  pick(wx: number, wy: number, tolPx: number): PickHit | null {
    // Under `off` the gate holds for the `off` pieces alone, so the layers
    // ask no other piece.
    if (this.disposed) return null;
    const over = this.screen.pickAt(wx, wy, tolPx);
    const under = this.sea.pickAt(wx, wy, tolPx);
    if (!over) return under;
    if (!under) return over;
    const dOver = Math.hypot(over.x - wx, over.y - wy);
    const dUnder = Math.hypot(under.x - wx, under.y - wy);
    return dUnder < dOver ? under : over;
  }

  /** Offers the small marks of both tiers under a world point (R2851):
   *  a wreck and a point of interest that pass the gate. */
  markPick(wx: number, wy: number, margin: number, out: SmallMark): void {
    if (this.disposed) return;
    this.screen.markPickAt(wx, wy, margin, out);
    this.sea.markPickAt(wx, wy, margin, out);
  }

  // -- life ----------------------------------------------------------------

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
    }
    this.dropGlyphs?.();
    this.dropRepack?.();
    for (const p of this.mounted) p.unmount();
    this.mounted.clear();
    this.sea.dispose();
    this.screen.dispose();
    setHostVisible(this.d.host?.root ?? null, false);
  }

  // -- the private half ----------------------------------------------------

  /** The layer that draws a canvas piece: the one of its tier. */
  private layerOf(p: Piece): ChartLayer {
    return p.tier === 'sea' ? this.sea : this.screen;
  }

  private sheetNow(): boolean {
    return this.d.sheet ? this.d.sheet() : isSheet();
  }

  /** The common gate, in the order the cheapest test comes first. */
  private gateOf(p: AnyPiece): boolean {
    const off = this.setting === 'off';
    if (this.broken.has(p) || (off && !this.keepSet.has(p) && p !== this.heatKept)) return false;
    if (this.only !== null && !this.only.has(p.id)) return false;
    if (this.hidden.has(p.id)) return false;
    // Every piece id is a key of the pack, so `t` always stands.
    const t = this.ctx.cfg.pieces[p.id];
    if (!(this.ctx.sheet ? t.mobile : t.desktop)) return false;
    if (this.ctx.zoom < t.zoomMin || this.ctx.zoom > t.zoomMax) return false;
    // Under `off` no reading is on, and the pack's `off: true` stands in for
    // it; balancegen holds such a piece on in both readings. A Chart theme
    // never reaches the gate of an `off` piece, under any setting: the map
    // markers always show (R1294).
    if (!off) {
      const said = this.keepSet.has(p) ? undefined : this.overlay?.[p.id]?.[this.drawn]?.on;
      if (!(said ?? t[this.drawn].on)) return false;
    }
    if (this.ctx.filming && p.film !== 'keep') return false;
    if (!p.visible) return true;
    // No `guard` here: the gate runs for every piece on every frame, and a
    // closure for each would be garbage (§10.3).
    try {
      return p.visible(this.ctx);
    } catch (err) {
      this.fail(p, 'visible', err);
      return false;
    }
  }

  /**
   * Runs one step of a piece and answers whether it completed.
   *
   * A throw marks the piece broken and is written once to the console with
   * the piece's id and the step, so a report can name it; nothing else in
   * the frame is touched.
   */
  private guard(p: AnyPiece, phase: string, step: () => void): boolean {
    try {
      step();
      return true;
    } catch (err) {
      this.fail(p, phase, err);
      return false;
    }
  }

  /** Marks a piece broken and says so once. The layers call it too. */
  fail(p: AnyPiece, phase: string, err: unknown): void {
    if (this.broken.has(p)) return;
    this.broken.add(p);
    this.gateOn.set(p, false);
    if (!isDomPiece(p) && p.pack === 'static') this.layerOf(p).markDirty();
    console.error(`chart: piece ${p.id} failed in ${phase} and is off until the next hello`, err);
  }

  /**
   * Narrows the furniture to the pieces a `chart` knob names (§17.2).
   *
   * null lets every piece through. The gate reads the set on the next
   * frame, and the layers repack on the edge, so nothing else is touched.
   */
  setOnly(only: ReadonlySet<string> | null): void {
    this.only = only;
  }

  /**
   * Hides the pieces of the groups the reader turned off in the VIEW panel
   * (R1812, round 086 card 15): `[chart.groups]` names the pieces of NAMES,
   * TRACKS and CARDS. The gate reads the set on the next frame, as it reads
   * `setOnly`. A piece of the setting `off` is in no group (balancegen).
   *
   * The scene calls it on every frame. The settings store hands over a new
   * list for a new pick and never changes a list in place, so the list it
   * handed over last is answered by one identity test and builds no key.
   *
   * A group with a `flag` hides its pieces only while the world latched
   * that flag (R2702): the panel lists no pill for the group in another
   * world, so a pick stored there must not hide a piece where no pill can
   * show it again. A new hello reads the list again.
   *
   * The CURRENTS and DEPTH heat maps show their group while the pill hides
   * it (R2870): the player asked for the field by name. The heat mode is
   * part of the identity test, so a mode change reads the list again.
   */
  setHidden(groups: readonly string[]): void {
    const state = this.ctx.state;
    const shown = heatMode(state.heatmap).flag;
    if (groups === this.hiddenList && shown === this.hiddenHeat) return;
    this.hiddenList = groups;
    this.hiddenHeat = shown;
    const flags: Readonly<Record<string, string>> = this.ctx.cfg.groupFlags;
    if (groups.some((g) => (flags[g] ?? '') !== '' || g === shown)) {
      groups = groups.filter((g) => {
        if (g === shown) return false;
        const f = flags[g] ?? '';
        return f === '' || state.hasFeatureFlag(f as FeatureFlag);
      });
    }
    const key = groups.join(',');
    if (key === this.hiddenKey) return;
    this.hiddenKey = key;
    // The legend card shows its DEPTH and CURRENT sections while their
    // groups show (R2616), so a new pick is a reason for it.
    this.ctx.hiddenGroups = groups;
    this.invalidate('settings');
    const all: Readonly<Record<string, readonly string[]>> = this.ctx.cfg.groups;
    const out = new Set<string>();
    for (const g of groups) for (const slug of all[g] ?? []) out.add(slug);
    this.hidden = out;
  }

  /**
   * Lays a Chart theme over the pack (R806): per piece and per reading, the
   * keys the theme sets win over the pack's own. The first key is `on`; a
   * key the overlay leaves out keeps the pack's value, and null takes the
   * whole overlay away. The gate reads it on the next frame and the layers
   * repack on the edge, as they do for `setOnly`.
   */
  setOverlay(overlay: ChartPieceOverlay | null): void {
    this.overlay = overlay;
  }

  /** The ids of the pieces that threw since the last hello. */
  brokenIds(): string[] {
    return [...this.broken].map((p) => p.id);
  }

  /**
   * A DOM piece stands in the host while its gate holds, and not else. A
   * piece that mounts renders on the same tick, and one whose render
   * throws comes down again.
   */
  private syncDom(p: DomPiece, on: boolean): void {
    if (!on) {
      if (!this.mounted.has(p)) return;
      this.guard(p, 'unmount', () => p.unmount());
      this.mounted.delete(p);
      return;
    }
    if (!this.mounted.has(p)) {
      const root = this.d.host?.root;
      if (!root || !this.guard(p, 'mount', () => p.mount(root, this.ctx))) return;
      this.mounted.add(p);
      this.stale.add(p);
    }
    if (!this.stale.has(p)) return;
    this.stale.delete(p);
    this.built.add(p);
    if (!this.guard(p, 'render', () => p.render(this.ctx))) {
      this.guard(p, 'unmount', () => p.unmount());
      this.mounted.delete(p);
    }
  }

  /** The ramp of a reading change, off the frame's own clock. */
  private advanceFade(timeS: number): void {
    if (!this.fading) return;
    if (this.fadeArmed) {
      this.fadeArmed = false;
      this.fadeStartS = timeS;
    }
    const half = Math.max(1e-3, this.ctx.cfg.fadeSec / 2);
    const t = Math.max(0, timeS - this.fadeStartS);
    if (t < half) { this.setAlpha(1 - t / half); return; }
    if (this.setting !== 'off' && this.drawn !== this.setting) {
      this.drawn = this.setting;
      this.ctx.reading = this.drawn;
      this.invalidate('reading');
    }
    if (t >= half * 2) {
      this.fading = false;
      this.setAlpha(1);
      return;
    }
    this.setAlpha((t - half) / half);
  }

  /** The fade, over the six batches of each tier and the DOM host at once. */
  private setAlpha(a: number): void {
    this.sea.setAlpha(a);
    this.screen.setAlpha(a);
    setHostAlpha(this.d.host?.root ?? null, a);
  }

  /**
   * A reading shows both layers and the host. `off` hides each of the three
   * unless an `off` piece in it passes the gate (R1294), so the loop neither
   * updates nor draws a layer that has nothing to show.
   */
  private applyVisible(): void {
    let sea = true;
    let screen = true;
    let host = true;
    if (this.setting === 'off') {
      sea = false;
      screen = false;
      host = false;
      for (const p of this.offAsked) {
        if (this.gateOn.get(p) !== true) continue;
        if (isDomPiece(p)) host = true;
        else if (p.tier === 'sea') sea = true;
        else screen = true;
      }
    }
    this.sea.setVisible(sea);
    this.screen.setVisible(screen);
    // The host is a DOM write, so it is written on the edge alone.
    if (host !== this.hostShown) {
      this.hostShown = host;
      setHostVisible(this.d.host?.root ?? null, host);
    }
  }

  /**
   * Follows the client heat map of the state (R2870): CURRENTS keeps card
   * 07 under `off`, DEPTH keeps card 09. A piece the map no longer keeps
   * leaves the gate under `off` at once, because `off` stops asking it.
   */
  private syncHeatKept(): void {
    const kind = this.d.state.heatmap;
    const kept = kind === HeatmapKind.HEATMAP_KIND_CURRENTS ? this.currentsPiece ?? null
      : kind === HeatmapKind.HEATMAP_KIND_DEPTH ? this.depthPiece ?? null : null;
    if (kept === this.heatKept) return;
    const before = this.heatKept;
    this.heatKept = kept;
    this.offAsked = kept === null || this.keepSet.has(kept) ? this.keep : [...this.keep, kept];
    if (this.setting === 'off' && before !== null && !this.keepSet.has(before)) {
      this.gateOn.set(before, false);
      this.applyVisible();
    }
  }

  /**
   * The terrain the pieces read: every ridge and every trench of this world,
   * and the name each one carries.
   *
   * Both are a function of the hello alone — the mask and the world id — so
   * they are built one time per world and never in a frame.
   */
  private readTerrain(): void {
    const blocked = this.d.scene.blocked;
    this.ridgeOf.clear();
    if (!blocked) { this.ctx.terrain = null; this.ctx.names = null; return; }
    const terrain = terrainFeatures(blocked, COLS, ROWS);
    this.ctx.terrain = terrain;
    for (const f of terrain.ridges) for (const c of f.cells) this.ridgeOf.set(c, f);
    this.ctx.names = assignFeatureNames(
      [...terrain.ridges, ...terrain.trenches],
      {
        ridge: RIDGE_NAMES, trench: TRENCH_NAMES,
        bank: BANK_NAMES, region: REGION_NAMES,
      },
      this.d.state.hello?.worldId ?? 0,
      trenchGrants(terrain.trenches, this.d.state.hello?.namedTrenches ?? []),
    );
  }
}
